import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import Header from "../components/app/Header";
import { toast } from "sonner";
import ContactDetailPanel from "../components/app/ContactDetailPanel";
import SendCampaignDialog from "../components/app/SendCampaignDialog";
import FieldFillPill from "../components/app/FieldFillPill";
import FieldFillCounter from "../components/app/FieldFillCounter";
import { Gem, Loader2, MessageSquare, Ban, Send, RefreshCw, Award } from "lucide-react";

const THRESHOLDS = [90, 180, 365];

function inr(n) {
  const v = Number(n || 0);
  return "₹" + v.toLocaleString("en-IN");
}

export default function Goldmine() {
  const [threshold, setThreshold] = useState(180);
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [openContact, setOpenContact] = useState(null);
  const [conn, setConn] = useState(null);
  const [showSend, setShowSend] = useState(false);
  const [sendInitial, setSendInitial] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([
        api.get(`/goldmine/summary?threshold_days=${threshold}`),
        api.get(`/goldmine/list?status=dormant&threshold_days=${threshold}`),
      ]);
      setSummary(s.data);
      setEntries(l.data.entries || []);
    } catch (e) {
      setEntries([]); setSummary({});
    }
  }, [threshold]);

  useEffect(() => {
    load();
    api.get("/employz/connection").then((r) => setConn(r.data)).catch(() => setConn({ status: "not_connected" }));
  }, [load]);

  const rescan = async () => {
    setScanning(true);
    try {
      await api.post(`/goldmine/scan?threshold_days=${threshold}`);
      toast.success("Scan complete.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed.");
    } finally { setScanning(false); }
  };

  const dismiss = async (id) => {
    const reason = window.prompt("Dismiss reason? (not_relevant / bad_customer / other)", "other");
    if (!reason) return;
    try {
      await api.post(`/goldmine/${id}/dismiss`, { reason });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Dismiss failed."); }
  };

  const openMessage = async (entry) => {
    try {
      const r = await api.get(`/employz/contacts-table`);
      const c = (r.data?.contacts || []).find((x) => x.ghl_contact_id === entry.ghl_contact_id);
      if (c) setOpenContact(c);
      else toast.error("Contact not found — sync may be stale.");
    } catch (e) { toast.error("Could not open contact."); }
  };

  const reactivateAll = async () => {
    try {
      const r = await api.post(`/goldmine/reactivate-prefill?threshold_days=${threshold}`);
      if (!r.data.campaign_id) {
        toast.error("No active campaign yet — register one on the Campaigns tab first.");
        return;
      }
      setSendInitial({
        campaign_id: r.data.campaign_id,
        contact_ids: r.data.contact_ids,
        context: r.data.context,
        segment: null,
      });
      setShowSend(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Prefill failed."); }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-7xl mx-auto px-6 md:px-10 pt-8 pb-16">
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-[0.28em] text-emerald-400">// Employz Intelligence / Repeat-Business Goldmine</div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl tracking-tighter font-black text-white flex items-center gap-3">
            <Gem className="w-9 h-9 text-amber-400" />
            Repeat-Business Goldmine
          </h1>
          <p className="mt-2 text-zinc-500 text-sm max-w-2xl">People who paid you once are the cheapest revenue you&apos;ll ever earn.</p>
        </div>

        {/* Headline */}
        {summary && (
          <div className="rounded-2xl border border-amber-500/20 bg-gradient-to-br from-amber-950/20 via-zinc-950 to-zinc-950 p-6 mb-6" data-testid="goldmine-headline">
            <div className="text-2xl md:text-3xl text-white font-display font-black tracking-tight">
              <span className="text-amber-300" data-testid="goldmine-dormant-count">{summary.dormant_count || 0}</span> customers already bought from you —{" "}
              <span className="text-emerald-300" data-testid="goldmine-total-value">{inr(summary.total_dormant_value)}</span> of proven business.
            </div>
            <div className="mt-2 text-sm text-zinc-400">Quiet for {threshold}+ days.</div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Dormancy threshold</div>
              {THRESHOLDS.map((t) => (
                <button key={t} onClick={() => setThreshold(t)} data-testid={`goldmine-threshold-${t}`}
                  className={`text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full border ${threshold === t ? "border-amber-500/60 bg-amber-500/10 text-amber-200" : "border-zinc-800 text-zinc-500"}`}>
                  {t}d
                </button>
              ))}
              <button onClick={rescan} disabled={scanning} data-testid="goldmine-rescan" className="ml-auto inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] px-2.5 py-1.5 border border-zinc-800 text-zinc-300 rounded">
                {scanning ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Rescan
              </button>
              <button onClick={reactivateAll} disabled={!summary.dormant_count} data-testid="goldmine-reactivate-all"
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] px-4 py-2 border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 rounded disabled:opacity-40">
                <Send className="w-3.5 h-3.5" /> Reactivate all
              </button>
            </div>
            {summary.reactivated_count > 0 && (
              <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-emerald-300" data-testid="goldmine-reactivated-counter">
                <Award className="w-3.5 h-3.5" /> {summary.reactivated_count} reactivated so far
              </div>
            )}
            <div className="mt-3">
              <FieldFillCounter testid="goldmine-field-fill-counter" />
            </div>
          </div>
        )}

        {/* Entries */}
        {entries === null ? (
          <div className="text-sm text-zinc-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>
        ) : entries.length === 0 ? (
          <div className="rounded-lg border border-dashed border-zinc-800 py-10 text-center text-sm text-zinc-500">
            No dormant customers at this threshold. Try 90 or 180 days.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="goldmine-entries">
            {entries.map((e) => (
              <div key={e.id} data-testid={`goldmine-entry-${e.id}`} className="rounded-xl border border-zinc-900 bg-zinc-950/60 p-4 hover:border-amber-500/30 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="text-white font-medium truncate">{e.contact_name}</div>
                  {e.band && <span className={`text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded border ${e.band === "hot" ? "border-rose-500/40 text-rose-300" : e.band === "warm" ? "border-amber-500/40 text-amber-300" : e.band === "cool" ? "border-sky-500/40 text-sky-300" : "border-zinc-700 text-zinc-400"}`}>{e.band}</span>}
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <span className="text-emerald-300 font-display font-black text-xl">{inr(e.total_past_value)}</span>
                  {e.times_bought > 1 && (
                    <span className="text-[10px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-full border border-amber-500/30 text-amber-300">bought {e.times_bought}x</span>
                  )}
                </div>
                {e.last_purchase_title && (
                  <div className="mt-1 text-xs text-zinc-500 truncate">Last: {e.last_purchase_title}{e.last_won_date ? ` · ${new Date(e.last_won_date).toLocaleDateString()}` : ""}</div>
                )}
                <div className="mt-1 text-[11px] text-zinc-600">Dormant {e.dormant_days || 0} days</div>
                <FieldFillPill ghlContactId={e.ghl_contact_id} testid={`goldmine-fills-${e.id}`} />
                <div className="mt-3 flex items-center gap-2">
                  <button onClick={() => openMessage(e)} data-testid={`goldmine-message-${e.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2.5 py-1.5 border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 rounded"><MessageSquare className="w-3 h-3" /> Message</button>
                  <button onClick={() => dismiss(e.id)} data-testid={`goldmine-dismiss-${e.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-1.5 border border-zinc-800 text-zinc-500 rounded"><Ban className="w-3 h-3" /> Dismiss</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {openContact && conn && (
          <ContactDetailPanel contact={openContact} connection={conn} onClose={() => setOpenContact(null)} onScoreUpdated={load} />
        )}
        <SendCampaignDialog open={showSend} initial={sendInitial} onClose={() => { setShowSend(false); setSendInitial(null); load(); }} />
      </main>
    </div>
  );
}
